import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Activity, Clock, Award, LogIn, LogOut, User, ChevronDown, Key, Lock, Mail, Info, Database, BarChart3, Shield } from "lucide-react";
import PriceCard from "../components/PriceCard";
import SignalCard from "../components/SignalCard";
import AlertBadge from "../components/AlertBadge";
import AlertSettings from "../components/AlertSettings";
import NewsTicker from "../components/NewsTickerComponent";
import NewsTab from "../components/NewsTab";
import HistoricalChart from "../components/HistoricalChart";
import SignalHistory from "../components/SignalHistory";
import PerformanceTracker from "../components/PerformanceTracker";
import PerformanceStats from "../components/PerformanceStats";
import MetaDataTable from "../components/MetaDataTable";
import { MtfAnalysisTab } from "../components/MtfAnalysis";
import SystemMonitorTab from "../components/SystemStatus/SystemMonitorTab";
import ModelMetricsTab from "../components/ModelMetrics/ModelMetricsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import LoginModal from "../components/LoginModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import ForceChangePasswordModal from "../components/ForceChangePasswordModal";
import ContactModal from "../components/ContactModal";
import MultiLLMChatbot from "../components/MultiLLMChatbot";
import { TradingAssistantButton, TradingAssistantModal } from "../components/TradingAssistant";
import { SREAgentButton, SREAgentModal } from "../components/SREAgent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORTED_COINS = ["BTC", "ETH", "SOL", "ADA", "XRP", "THETA", "TFUEL", "DOT", "AVAX", "NMR", "BNB", "LTC", "TRX", "BCH", "HYPE", "XMR", "HBAR", "UNI", "LINK", "FET", "FIL", "SHIB", "DOGE", "LEO", "XLM", "SAND", "RENDER", "SUI", "DAI"];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, mustChangePassword, logout, loading: authLoading } = useAuth();
  const [prices, setPrices] = useState(null);
  const [signals, setSignals] = useState({});
  const [mlSignals, setMlSignals] = useState({});
  const [loading, setLoading] = useState(true);
  const [signalLoading, setSignalLoading] = useState({});
  const [activeSignalType, setActiveSignalType] = useState('basic'); // 'basic' or 'ml'
  const [trainingStatus, setTrainingStatus] = useState({});
  const [signalGenerationStatus, setSignalGenerationStatus] = useState({});
  const [backtestStatus, setBacktestStatus] = useState({});
  const [backtestPeriod, setBacktestPeriod] = useState(30); // Max 30 days (CMC data limit)
  const [trainedCoins, setTrainedCoins] = useState([]); // Coins with trained models
  const [fearGreedIndex, setFearGreedIndex] = useState(null);
  const [globalMetrics, setGlobalMetrics] = useState(null);
  const [selectedCoins, setSelectedCoins] = useState(["BTC", "ETH"]);
  const [manualStats, setManualStats] = useState(null);
  const [backtestStats, setBacktestStats] = useState(null);
  const [closingManualPositions, setClosingManualPositions] = useState(false);
  const [closingAutoPositions, setClosingAutoPositions] = useState(false);
  const [autoOpenPositions, setAutoOpenPositions] = useState(0);
  const [autoCapital, setAutoCapital] = useState(null); // Effective capital for compounding
  const [statsPeriod, setStatsPeriod] = useState(() => {
    // Persist user's selected period in localStorage; default 60 days
    const saved = localStorage.getItem("statsPeriod");
    if (saved !== null) {
      const n = parseInt(saved);
      if ([0, 7, 30, 60, 90].includes(n)) return n;
    }
    return 60;
  }); // Date filter for Automated Trading Stats: 7|30|60|90|0 (0 = All time)
  const statsPeriodMounted = useRef(false); // Skip the initial-mount run of the statsPeriod effect (avoids duplicate fetch with initializeDashboard)
  const [priceLoadError, setPriceLoadError] = useState(false); // Track if prices failed to load
  const [performanceRefreshTrigger, setPerformanceRefreshTrigger] = useState(0); // Trigger for PerformanceTracker refresh
  // Auth modals
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showForceChangePasswordModal, setShowForceChangePasswordModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [smartSelectLoading, setSmartSelectLoading] = useState(false);
  
  // AI Research Agent state
  const [showAIResearchInput, setShowAIResearchInput] = useState(false);
  const [showAIResearchResult, setShowAIResearchResult] = useState(false);
  const [aiResearchQuery, setAiResearchQuery] = useState('');
  const [aiResearchLoading, setAiResearchLoading] = useState(false);
  const [aiResearchResult, setAiResearchResult] = useState(null);
  
  // Trading Assistant state
  const [showTradingAssistant, setShowTradingAssistant] = useState(false);
  const [showSREAgent, setShowSREAgent] = useState(false);

  // Show force change password modal when mustChangePassword is true
  useEffect(() => {
    if (isAuthenticated && mustChangePassword) {
      setShowForceChangePasswordModal(true);
    }
  }, [isAuthenticated, mustChangePassword]);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/prices`);
      const priceData = response.data;
      setPrices(priceData);
      setPriceLoadError(false); // Clear error on success
      
      // NOTE: We no longer auto-remove coins from selection when price data is missing
      // This was causing issues where Smart Select coins would disappear
      // Instead, just log a warning for debugging
      const coinsWithoutData = selectedCoins.filter(coin => !priceData[coin]);
      if (coinsWithoutData.length > 0) {
        console.warn(`Price data unavailable for: ${coinsWithoutData.join(', ')}`);
        // Don't remove from selection - let user see which coins have issues
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
      setPriceLoadError(true); // Set error state
      toast.error("Failed to fetch crypto prices. Click retry to try again.");
    }
  };

  const fetchManualStats = async () => {
    try {
      const response = await axios.get(`${API}/manual/stats`);
      setManualStats(response.data);
    } catch (error) {
      console.error("Error fetching manual stats:", error);
    }
  };

  const fetchBacktestStats = async () => {
    try {
      const response = await axios.get(`${API}/backtest/stats`);
      setBacktestStats(response.data);
    } catch (error) {
      console.error("Error fetching backtest stats:", error);
    }
  };

  const closeAllManualPositions = async () => {
    if (closingManualPositions) return;
    
    const confirmed = window.confirm(
      `⚠️ Close All Manual Positions\n\n` +
      `This will close all open manual trading positions at current market prices.\n\n` +
      `Continue?`
    );
    
    if (!confirmed) return;
    
    setClosingManualPositions(true);
    try {
      const response = await axios.post(`${API}/manual/close-all`);
      if (response.data.positions_closed > 0) {
        toast.success(`✅ Closed ${response.data.positions_closed} positions. Total P/L: $${response.data.total_pnl.toFixed(2)}`);
        fetchManualStats(); // Refresh stats
      } else {
        toast.info("No open manual positions to close");
      }
    } catch (error) {
      console.error("Error closing manual positions:", error);
      toast.error("Failed to close manual positions");
    } finally {
      setClosingManualPositions(false);
    }
  };

  const fetchAutoOpenPositions = async () => {
    try {
      const url = statsPeriod && statsPeriod > 0
        ? `${API}/automation/status?days=${statsPeriod}`
        : `${API}/automation/status`;
      const response = await axios.get(url);
      setAutoOpenPositions(response.data.open_positions || 0);
      // Store capital info for compounding display
      if (response.data.capital) {
        setAutoCapital(response.data.capital);
      }
    } catch (error) {
      console.error("Error fetching auto positions:", error);
    }
  };

  const closeAllAutoPositions = async () => {
    if (closingAutoPositions) return;
    
    const confirmed = window.confirm(
      `⚠️ Close All Automated Positions\n\n` +
      `This will close ${autoOpenPositions} open automated trading positions at current market prices.\n\n` +
      `Continue?`
    );
    
    if (!confirmed) return;
    
    setClosingAutoPositions(true);
    try {
      const response = await axios.post(`${API}/automation/force-close-all`);
      if (response.data.positions_closed > 0) {
        toast.success(`⚡ Closed ${response.data.positions_closed} positions. Total P/L: $${response.data.total_pnl.toFixed(2)}`);
        fetchAutoOpenPositions(); // Refresh count
      } else {
        toast.info("No open automated positions to close");
      }
    } catch (error) {
      console.error("Error closing auto positions:", error);
      toast.error("Failed to close automated positions");
    } finally {
      setClosingAutoPositions(false);
    }
  };

  const fetchSignal = async (coin) => {
    setSignalLoading(prev => ({ ...prev, [coin]: true }));
    try {
      const response = await axios.get(`${API}/signals/${coin}`);
      setSignals(prev => ({ ...prev, [coin]: response.data }));
    } catch (error) {
      console.error(`Error fetching ${coin} signal:`, error);
      toast.error(`Failed to generate ${coin} signal`);
    } finally {
      setSignalLoading(prev => ({ ...prev, [coin]: false }));
    }
  };

  const fetchMLSignal = async (coin) => {
    setSignalLoading(prev => ({ ...prev, [coin]: true }));
    try {
      // Include MTF confirmation data for entry timing
      const response = await axios.get(`${API}/ml/signal/${coin}?include_mtf=true`);
      setMlSignals(prev => ({ ...prev, [coin]: response.data }));
      
      toast.success(`ML signal generated for ${coin}`);
    } catch (error) {
      console.error(`Error fetching ML signal for ${coin}:`, error);
      if (error.response?.status === 500) {
        toast.error(`ML models not trained for ${coin}. Click "Train Models" first.`);
      } else {
        toast.error(`Failed to generate ML signal for ${coin}`);
      }
    } finally {
      setSignalLoading(prev => ({ ...prev, [coin]: false }));
    }
  };

  const trainModel = async (coin) => {
    // Force reset training status to show progress bar
    setTrainingStatus(prev => ({ 
      ...prev, 
      [coin]: { status: "training", progress: 0 } 
    }));
    
    // Small delay to ensure UI updates
    await new Promise(resolve => setTimeout(resolve, 100));
    
    toast.info(`Training ML models for ${coin}... This takes ~50 seconds.`);
    
    try {
      const response = await axios.post(`${API}/ml/train/${coin}`);
      
      // Show data range info if available
      if (response.data?.data_range) {
        const dr = response.data.data_range;
        toast.success(`Training started for ${coin} (Data: ${dr.start_date} to ${dr.end_date}, ${dr.data_points} points)`);
      } else {
        toast.success(`Training started for ${coin}`);
      }
      
      // Poll training status from backend
      const startTime = Date.now();
      const maxDuration = 120000; // 2 minute safety timeout
      
      const pollInterval = setInterval(async () => {
        const elapsed = Date.now() - startTime;
        
        try {
          // Poll the new training status endpoint
          const statusResponse = await axios.get(`${API}/ml/training-status`);
          const coinStatus = statusResponse.data[coin];
          
          if (coinStatus) {
            // Update with actual status from backend
            setTrainingStatus(prev => ({ 
              ...prev, 
              [coin]: coinStatus
            }));
            
            if (coinStatus.status === 'completed') {
              clearInterval(pollInterval);
              toast.success(`${coin} models trained! You can now generate ML signals.`);
            } else if (coinStatus.status === 'error') {
              clearInterval(pollInterval);
              toast.error(`Training failed for ${coin}: ${coinStatus.error || 'Unknown error'}`);
            }
          } else {
            // Fallback: use time-based progress if backend doesn't have status yet
            const progress = Math.min(95, Math.floor((elapsed / 55000) * 100));
            setTrainingStatus(prev => ({ 
              ...prev, 
              [coin]: { status: "training", progress }
            }));
          }
        } catch (err) {
          console.error('Error polling training status:', err);
        }
        
        // Safety timeout - stop polling after 2 minutes
        if (elapsed > maxDuration) {
          clearInterval(pollInterval);
          setTrainingStatus(prev => ({ 
            ...prev, 
            [coin]: { status: "completed", progress: 100 } 
          }));
          toast.success(`${coin} models should be trained now.`);
        }
      }, 3000); // Poll every 3 seconds
      
    } catch (error) {
      console.error(`Error training ${coin}:`, error);
      setTrainingStatus(prev => ({ ...prev, [coin]: { status: "error", progress: 0 } }));
      toast.error(`Failed to train models for ${coin}`);
    }
  };

  // Fetch Fear & Greed Index
  const fetchFearGreedIndex = async () => {
    try {
      const response = await axios.get(`${API}/fear-greed-index`);
      setFearGreedIndex(response.data);
    } catch (error) {
      console.error("Error fetching Fear & Greed Index:", error);
    }
  };

  // Fetch Global Market Metrics
  const fetchGlobalMetrics = async () => {
    try {
      const response = await axios.get(`${API}/global-metrics`);
      setGlobalMetrics(response.data);
    } catch (error) {
      console.error("Error fetching Global Metrics:", error);
    }
  };
  
  // Fetch trained coins list
  const fetchTrainedCoins = async () => {
    try {
      const response = await axios.get(`${API}/ml/status`);
      setTrainedCoins(response.data.trained_coins || []);
    } catch (error) {
      console.error("Error fetching trained coins:", error);
    }
  };
  
  useEffect(() => {
    const initializeDashboard = async () => {
      setLoading(true);
      
      // Load saved backtest period from localStorage
      const savedPeriod = localStorage.getItem("backtestPeriod");
      if (savedPeriod && [7, 14, 30].includes(parseInt(savedPeriod))) {
        setBacktestPeriod(parseInt(savedPeriod));
      }
      
      await fetchPrices();
      await fetchFearGreedIndex();
      await fetchGlobalMetrics();
      await fetchTrainedCoins();
      await fetchManualStats();
      await fetchBacktestStats();
      await fetchAutoOpenPositions();
      setLoading(false);
    };

    initializeDashboard();

    // Refresh prices every 30 seconds (optimized for API rate limits)
    const priceInterval = setInterval(fetchPrices, 15000); // 15 seconds for real-time trading
    // Fear & Greed updates slowly
    const fgInterval = setInterval(fetchFearGreedIndex, 900000);
    // Global metrics every 5 mins
    const globalMetricsInterval = setInterval(fetchGlobalMetrics, 300000);
    // Trained coins check every 60s (no CMC API call)
    const trainedInterval = setInterval(fetchTrainedCoins, 60000);
    // Manual stats every 30s (no CMC API call)
    const manualStatsInterval = setInterval(fetchManualStats, 30000);
    // Backtest stats every 60s (no CMC API call)
    const backtestStatsInterval = setInterval(fetchBacktestStats, 60000);
    // Auto positions every 15s (no CMC API call)
    const autoPositionsInterval = setInterval(fetchAutoOpenPositions, 15000);

    return () => {
      clearInterval(priceInterval);
      clearInterval(fgInterval);
      clearInterval(globalMetricsInterval);
      clearInterval(trainedInterval);
      clearInterval(manualStatsInterval);
      clearInterval(backtestStatsInterval);
      clearInterval(autoPositionsInterval);
    };
  }, []);

  // Re-fetch automated capital whenever the user changes the date filter.
  // Skip the very first run on mount — initializeDashboard already fetched it,
  // and we don't want a duplicate network call.
  useEffect(() => {
    if (!statsPeriodMounted.current) {
      statsPeriodMounted.current = true;
      return;
    }
    localStorage.setItem("statsPeriod", String(statsPeriod));
    fetchAutoOpenPositions();
  }, [statsPeriod]); // eslint-disable-line

  const toggleCoin = (coin) => {
    if (selectedCoins.includes(coin)) {
      setSelectedCoins(selectedCoins.filter(c => c !== coin));
    } else {
      setSelectedCoins([...selectedCoins, coin]);
      // Only auto-generate signal if authenticated
      if (!signals[coin] && isAuthenticated) {
        fetchSignal(coin);
      }
    }
  };

  // Smart select top 5 coins based on liquidity + volatility
  const handleSmartSelect = async () => {
    if (smartSelectLoading) return;
    
    setSmartSelectLoading(true);
    try {
      // Use all supported coins for smart selection
      const response = await axios.post(`${API}/smart-select-coins`, SUPPORTED_COINS);
      
      if (response.data.status === 'success' && response.data.selected_coins) {
        const smartCoins = response.data.selected_coins;
        
        // Clear previous selection and set the smart selected coins
        setSelectedCoins(smartCoins);
        
        // Also update the watchlist in Settings (for signal generation)
        try {
          // First get current settings
          const settingsResponse = await axios.get(`${API}/settings`);
          const currentSettings = settingsResponse.data;
          
          // Update watchlist with smart selected coins
          await axios.post(`${API}/settings`, {
            ...currentSettings,
            watchlist: smartCoins
          });
          
          // Show success message with selected coins
          toast.success(
            `🎯 Smart Selected: ${smartCoins.join(', ')} (Watchlist updated)`,
            { duration: 4000 }
          );
          
          // Show data quality warning toast ONLY if there are issues (Option 4)
          const dataQuality = response.data.data_quality;
          const warningMsg = response.data.warning;
          if (warningMsg) {
            toast.warning(warningMsg, { duration: 5000 });
          } else if (dataQuality && dataQuality.api_failures > 0) {
            toast.warning(
              `⚠️ ${dataQuality.api_failures}/${dataQuality.coins_requested} API calls failed - using fallback data`,
              { duration: 5000 }
            );
          }
        } catch (settingsError) {
          // If settings update fails, still show success for selection
          console.warn('Could not update watchlist in settings:', settingsError);
          toast.success(
            `🎯 Smart Selected: ${smartCoins.join(', ')}`,
            { duration: 4000 }
          );
          
          // Show data quality warning even if settings update failed
          const dataQuality = response.data.data_quality;
          const warningMsg = response.data.warning;
          if (warningMsg) {
            toast.warning(warningMsg, { duration: 5000 });
          } else if (dataQuality && dataQuality.api_failures > 0) {
            toast.warning(
              `⚠️ ${dataQuality.api_failures}/${dataQuality.coins_requested} API calls failed - using fallback data`,
              { duration: 5000 }
            );
          }
        }
        
        // Log ranking details for transparency
        console.log('🎯 Smart Selection Ranking:', response.data.ranking);
        console.log('📊 Data Quality:', response.data.data_quality);
      } else {
        toast.error('Failed to smart select coins');
      }
    } catch (error) {
      console.error('Smart select error:', error);
      toast.error(error.response?.data?.detail || 'Failed to smart select coins');
    } finally {
      setSmartSelectLoading(false);
    }
  };

  // AI Research Agent handler
  const handleAIResearch = async () => {
    if (!aiResearchQuery.trim()) {
      toast.error('Please enter a research query');
      return;
    }
    
    setAiResearchLoading(true);
    setShowAIResearchInput(false);
    
    try {
      const response = await axios.post(`${API}/ai-research`, {
        query: aiResearchQuery.trim()
      });
      
      if (response.data.status === 'success') {
        setAiResearchResult(response.data);
        setShowAIResearchResult(true);
        toast.success('🔬 Research analysis complete!', { duration: 3000 });
      } else {
        toast.error('Research agent returned an error');
      }
    } catch (error) {
      console.error('AI Research error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get research analysis');
    } finally {
      setAiResearchLoading(false);
    }
  };

  // Format AI response (handles both text and JSON)
  const formatAIResponse = (response) => {
    if (!response) return null;
    
    // Try to parse as JSON
    try {
      const jsonData = typeof response === 'string' ? JSON.parse(response) : response;
      if (typeof jsonData === 'object') {
        return (
          <pre className="bg-black/30 p-4 rounded-lg overflow-auto max-h-96 text-sm whitespace-pre-wrap">
            {JSON.stringify(jsonData, null, 2)}
          </pre>
        );
      }
    } catch (e) {
      // Not JSON, treat as text
    }
    
    // Return as formatted text with line breaks preserved
    return (
      <div className="prose prose-invert max-w-none">
        {response.split('\n').map((line, i) => (
          <p key={i} className="mb-2 text-sm leading-relaxed">
            {line || <br />}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Breaking News Ticker */}
      <NewsTicker onArticleClick={() => {
        // Scroll to news tab when ticker is clicked
        const newsTab = document.querySelector('[data-testid=\"tab-news\"]');
        if (newsTab) newsTab.click();
      }} />
      
      {/* Risk Disclaimer Banner */}
      <div className="bg-red-500/15 border-b border-red-500/30">
        <div className="container mx-auto px-4 py-2">
          <p className="text-xs text-white/90 leading-relaxed">
            <span className="font-semibold">Disclaimer:</span> Cryptocurrencies are highly speculative and volatile assets. This platform is a personal research, live simulation, signal generation and quantitative analysis tool for private non-commercial use. It is not offered to the public and does not provide investment advice, trading advice, or personal recommendations under UK financial regulation. The platform provides general market analysis, long-only algorithmic indicators, simulated outputs for educational and informational purposes only. It is not a trading platform, does not execute trades and has no access to user funds or exchange accounts. All information forms part of a paper-trading and signal simulation system. Users must conduct their own research. Data, prices, and outputs may be delayed or inaccurate while the platform remains in testing/simulation mode. © 2026 Signal4Strategy. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Header */}
      <div className="glass-card border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold heading-font tracking-tight" data-testid="app-title">
                Signal_X
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Crypto Trading Signals</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Settings Button */}
              <button
                onClick={() => {
                  if (!isAuthenticated) {
                    setShowLoginModal(true);
                    toast.info('Please login to access Settings');
                    return;
                  }
                  navigate('/settings');
                }}
                className="glass-card px-4 py-2 rounded-lg hover:bg-white/10 transition-all flex items-center gap-2 border border-white/10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-medium">Settings</span>
              </button>
              
              {/* Contact Us Button */}
              <button
                onClick={() => setShowContactModal(true)}
                className="glass-card px-4 py-2 rounded-lg hover:bg-white/10 transition-all flex items-center gap-2 border border-white/10"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium">Contact Us</span>
              </button>
              
              {/* Alert Settings */}
              <AlertSettings />
              
              {/* Login / User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="glass-card px-4 py-2 rounded-lg hover:bg-white/10 transition-all flex items-center gap-2 border border-primary/30 bg-primary/10"
                  >
                    <User className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">{user?.username || 'User'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 glass-card rounded-lg border border-white/10 overflow-hidden z-50">
                      <div className="px-4 py-2 border-b border-white/10">
                        <p className="text-sm font-medium">{user?.username}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowChangePasswordModal(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2"
                      >
                        <Key className="w-4 h-4" />
                        Change Password
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                          toast.success('Logged out successfully');
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2 text-red-400"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="glass-card px-4 py-2 rounded-lg hover:bg-primary/20 transition-all flex items-center gap-2 border border-primary/30"
                >
                  <LogIn className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">Login</span>
                </button>
              )}
              
              {/* Fear & Greed Index Display */}
              {fearGreedIndex && (
                <div className="glass-card p-3 border-white/10 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Market Sentiment</div>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold heading-font">
                      {fearGreedIndex.value}
                    </div>
                    <div>
                      <div className={`text-xs font-semibold ${
                        fearGreedIndex.value <= 25 ? 'text-[#FF0055]' :
                        fearGreedIndex.value <= 45 ? 'text-[#FF8C00]' :
                        fearGreedIndex.value <= 55 ? 'text-[#FFD600]' :
                        fearGreedIndex.value <= 75 ? 'text-[#00FF94]' :
                        'text-[#00FF94]'
                      }`}>
                        {fearGreedIndex.value <= 25 ? '🔴 Extreme Fear' :
                         fearGreedIndex.value <= 45 ? '🟠 Fear' :
                         fearGreedIndex.value <= 55 ? '🟡 Neutral' :
                         fearGreedIndex.value <= 75 ? '🟢 Greed' :
                         '🟢 Extreme Greed'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Global Market Overview */}
              {globalMetrics && globalMetrics.total_market_cap > 0 && (
                <div className="glass-card p-3 border-white/10 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-2">Market Overview</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">BTC Dom:</span>
                      <span className="font-semibold text-[#F7931A]">
                        {globalMetrics.btc_dominance}%
                        <span className={`ml-1 ${globalMetrics.btc_dominance_24h_change >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ({globalMetrics.btc_dominance_24h_change >= 0 ? '+' : ''}{globalMetrics.btc_dominance_24h_change}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">ETH Dom:</span>
                      <span className="font-semibold text-[#627EEA]">
                        {globalMetrics.eth_dominance}%
                        <span className={`ml-1 ${globalMetrics.eth_dominance_24h_change >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ({globalMetrics.eth_dominance_24h_change >= 0 ? '+' : ''}{globalMetrics.eth_dominance_24h_change}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">MCap:</span>
                      <span className="font-semibold text-data">
                        ${(globalMetrics.total_market_cap / 1e12).toFixed(2)}T
                        <span className={`ml-1 ${globalMetrics.total_market_cap_24h_change >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ({globalMetrics.total_market_cap_24h_change >= 0 ? '+' : ''}{globalMetrics.total_market_cap_24h_change}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">24h Vol:</span>
                      <span className="font-semibold text-data">
                        ${(globalMetrics.total_volume_24h / 1e9).toFixed(1)}B
                        <span className={`ml-1 ${globalMetrics.total_volume_24h_change >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ({globalMetrics.total_volume_24h_change >= 0 ? '+' : ''}{globalMetrics.total_volume_24h_change}%)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="live-indicator">
                <div className="w-2 h-2 bg-[#00FF94] rounded-full animate-pulse"></div>
                <span>LIVE</span>
              </div>
            </div>
          </div>
          
          {/* Coin Selector & ML Toggle */}
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {SUPPORTED_COINS.map(coin => (
                  <button
                    key={coin}
                    onClick={() => toggleCoin(coin)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      selectedCoins.includes(coin)
                        ? "bg-primary text-primary-foreground"
                        : "bg-white/5 text-muted-foreground hover:bg-white/10"
                    }`}
                    data-testid={`coin-selector-${coin.toLowerCase()}`}
                  >
                    {coin}
                  </button>
                ))}
                {/* Smart Select Button with Tooltip - Admin Only */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to use Smart Select');
                            return;
                          }
                          if (!isAdmin) {
                            toast.error('Smart Select is only available for admin users');
                            return;
                          }
                          handleSmartSelect();
                        }}
                        disabled={smartSelectLoading}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        data-testid="smart-select-btn"
                      >
                        {smartSelectLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Selecting...</span>
                          </>
                        ) : (
                          <>
                            🎯 Smart Select
                            <Info className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-white/10">
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">🎯 Smart Coin Selection</p>
                        <p className="text-xs text-cyan-300 font-medium">Optimized for 30-45 min intraday trading</p>
                        <p className="text-xs text-gray-300">Auto-selects top 5 coins using 5-min CMC data:</p>
                        <ul className="text-xs text-gray-300 list-disc list-inside space-y-1">
                          <li><span className="text-blue-400">30%</span> 30-min Price Volatility</li>
                          <li><span className="text-purple-400">25%</span> Volume Change Detection</li>
                          <li><span className="text-green-400">20%</span> Short-term Momentum</li>
                          <li><span className="text-yellow-400">15%</span> Liquidity (24h Volume)</li>
                          <li><span className="text-pink-400">10%</span> Trend Alignment + Acceleration</li>
                        </ul>
                        <p className="text-xs text-gray-400 italic">Filters out flat coins (&lt;0.3% volatility)</p>
                        {!isAuthenticated && (
                          <p className="text-xs text-yellow-400">⚠️ Login required (Admin only)</p>
                        )}
                        {isAuthenticated && !isAdmin && (
                          <p className="text-xs text-yellow-400">⚠️ Admin access required</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {/* Multi-LLM Chatbot Button */}
                <MultiLLMChatbot 
                  isAuthenticated={isAuthenticated}
                  isAdmin={isAdmin}
                  onLoginRequired={() => setShowLoginModal(true)}
                />
                
                {/* Trading Assistant Button */}
                <TradingAssistantButton 
                  onClick={() => setShowTradingAssistant(true)}
                  isAuthenticated={isAuthenticated}
                  isAdmin={isAdmin}
                  onLoginRequired={() => setShowLoginModal(true)}
                />

                {/* SRE Agent Button (admin-only) */}
                <SREAgentButton
                  onClick={() => setShowSREAgent(true)}
                  isAuthenticated={isAuthenticated}
                  isAdmin={isAdmin}
                  onLoginRequired={() => setShowLoginModal(true)}
                />
              </div>
              
              {/* Signal Type Tabs */}
              <div className="flex gap-3 mt-6 mb-4">
                <button
                  onClick={() => {
                    setActiveSignalType('basic');
                    toast.info('Switched to Basic Signals');
                  }}
                  className={`flex-1 px-6 py-3 rounded-lg text-lg font-bold transition-all ${
                    activeSignalType === 'basic'
                      ? 'bg-[#00FF94] text-black shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid="tab-basic-signals"
                >
                  📊 Basic Signals
                </button>
                <button
                  onClick={() => {
                    setActiveSignalType('ml');
                    toast.info('Switched to ML Signals');
                  }}
                  className={`flex-1 px-6 py-3 rounded-lg text-lg font-bold transition-all ${
                    activeSignalType === 'ml'
                      ? 'bg-[#00FF94] text-black shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid="tab-ml-signals"
                >
                  🤖 ML Signals
                </button>
                {/* AI Research Agent Button - Admin Only */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to use AI Research Agent');
                            return;
                          }
                          if (!isAdmin) {
                            toast.error('AI Research Agent is only available for admin users');
                            return;
                          }
                          setAiResearchQuery('');
                          setShowAIResearchInput(true);
                        }}
                        disabled={aiResearchLoading}
                        className={`flex-1 px-6 py-3 rounded-lg text-lg font-bold transition-all ${
                          aiResearchLoading
                            ? 'bg-[#00FF94] text-black shadow-lg cursor-not-allowed opacity-70'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        data-testid="ai-research-btn"
                      >
                        {aiResearchLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                            Analyzing...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            🔬 AI Research Analyst Agent <span className="text-xs opacity-70">(Multi-Asset)</span>
                            <Info className="w-5 h-5" />
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-white/10">
                      <div className="space-y-2">
                        <p className="font-semibold text-sm">🔬 AI Research Analyst Agent</p>
                        <p className="text-xs text-cyan-300 font-medium">Multi-asset research agent. Analyze Stocks, Crypto, ETFs, FX, Commodities, Indices & Futures.</p>
                        <p className="text-xs text-gray-300">Get deep analysis from AI agents including:</p>
                        <ul className="text-xs text-gray-300 list-disc list-inside space-y-1">
                          <li>Technical Indicator Analysis</li>
                          <li>Macro Context & News Sentiment</li>
                          <li>Portfolio Risk Assessment</li>
                          <li>Signal Trigger Recommendations</li>
                        </ul>
                        <p className="text-xs text-emerald-400 italic">Built using OpenAI Agent Builder platform</p>
                        {!isAuthenticated && (
                          <p className="text-xs text-yellow-400">⚠️ Login required (Admin only)</p>
                        )}
                        {isAuthenticated && !isAdmin && (
                          <p className="text-xs text-yellow-400">⚠️ Admin access required</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {/* ML Action Buttons - Only visible when ML tab is active */}
              {activeSignalType === 'ml' && (
                <div className="glass-card p-4 rounded-lg border-white/10 mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">ML Training & Analysis</h3>
                  <div className="flex gap-2 flex-wrap">
                    <button
                  onClick={async () => {
                    if (!isAuthenticated) {
                      setShowLoginModal(true);
                      toast.info('Please login to train models');
                      return;
                    }
                    console.log('🔵 TRAIN MODELS CLICKED');
                    console.log('🔵 Selected coins:', selectedCoins);
                      
                      if (selectedCoins.length === 0) {
                        toast.error('Please select at least one coin first');
                        return;
                      }
                      
                      // Check which coins need training (no ML signals yet)
                      const coinsToTrain = selectedCoins.filter(coin => !mlSignals[coin]);
                      console.log('🔵 Coins to train:', coinsToTrain);
                      console.log('🔵 Current ML signals:', Object.keys(mlSignals));
                      
                      if (coinsToTrain.length === 0) {
                        toast.info("All selected coins already have ML signals! Generating fresh signals...");
                        
                        // Generate fresh signals for all
                        for (let i = 0; i < selectedCoins.length; i += 3) {
                          const batch = selectedCoins.slice(i, i + 3);
                          await Promise.all(
                            batch.map(coin => fetchMLSignal(coin).catch(err => {
                              console.error(`Failed to generate signal for ${coin}:`, err);
                            }))
                          );
                        }
                        toast.success(`✅ Refreshed ML signals for ${selectedCoins.length} coins!`);
                        return;
                      }
                      
                      // Warn if training many coins
                      if (coinsToTrain.length > 5) {
                        const estimatedTime = Math.ceil(coinsToTrain.length / 3);
                        const confirmed = window.confirm(
                          `⚠️ Warning: You're about to train ${coinsToTrain.length} coins.\n\n` +
                          `• Estimated time: ~${estimatedTime} minutes\n` +
                          `• System will train 3 coins in parallel\n` +
                          `• Recommendation: Train 3-5 coins at a time for best performance\n\n` +
                          `Already trained: ${selectedCoins.length - coinsToTrain.length} coins (skipped)\n\n` +
                          `Continue anyway?`
                        );
                        if (!confirmed) return;
                      }
                      
                      if (selectedCoins.length > coinsToTrain.length) {
                        toast.success(`Skipped ${selectedCoins.length - coinsToTrain.length} already-trained coins`);
                      }
                      
                      // Use bulk training endpoint for multiple coins (more efficient)
                      if (coinsToTrain.length > 2) {
                        console.log('🔵 Using bulk training for', coinsToTrain.length, 'coins');
                        toast.info(`Starting bulk training for ${coinsToTrain.length} coins...`);
                        
                        // Set all to queued IMMEDIATELY so progress bars appear
                        const newStatuses = {};
                        coinsToTrain.forEach(coin => {
                          newStatuses[coin] = { status: "queued", progress: 0 };
                        });
                        console.log('🎯 Setting initial queued status:', newStatuses);
                        setTrainingStatus(prev => {
                          const updated = { ...prev, ...newStatuses };
                          console.log('🎯 New training status state:', updated);
                          return updated;
                        });
                        
                        // Force a small delay to ensure UI updates
                        await new Promise(resolve => setTimeout(resolve, 200));
                        
                        try {
                          console.log('🔵 Sending bulk training request for:', coinsToTrain);
                          const bulkResponse = await axios.post(`${API}/ml/train-bulk`, coinsToTrain);
                          console.log('🔵 Bulk training response:', bulkResponse.data);
                          
                          // Poll for all coins with progress tracking
                          let generatingSignals = false;
                          const pollInterval = setInterval(async () => {
                            try {
                              const statusResponse = await axios.get(`${API}/ml/training-status`);
                              const allStatuses = statusResponse.data;
                              
                              console.log('📊 Polling training status:', allStatuses);
                              
                              // Update all coin statuses with progress bars
                              setTrainingStatus(prev => {
                                const newStatus = { ...prev, ...allStatuses };
                                console.log('📊 Updated training status state:', newStatus);
                                return newStatus;
                              });
                              
                              // Check if all completed or errored
                              const allDone = coinsToTrain.every(coin => 
                                allStatuses[coin]?.status === 'completed' || allStatuses[coin]?.status === 'error'
                              );
                              
                              if (allDone && !generatingSignals) {
                                clearInterval(pollInterval);
                                generatingSignals = true;
                                
                                const completed = coinsToTrain.filter(c => allStatuses[c]?.status === 'completed').length;
                                const failed = coinsToTrain.filter(c => allStatuses[c]?.status === 'error');
                                
                                if (failed.length > 0) {
                                  // Show warning with failed coins
                                  const failedDetails = failed.map(c => {
                                    const err = allStatuses[c]?.error || 'Unknown error';
                                    return `${c}: ${err}`;
                                  }).join(', ');
                                  toast.warning(
                                    `Training complete: ${completed}/${coinsToTrain.length} succeeded. Failed: ${failedDetails}`,
                                    { duration: 8000 }
                                  );
                                } else {
                                  toast.success(`Training complete! ${completed}/${coinsToTrain.length} coins trained successfully.`);
                                }
                                
                                // Refresh trained coins list
                                await fetchTrainedCoins();
                                
                                // Clear training status after longer delay if there were errors
                                setTimeout(() => setTrainingStatus({}), failed.length > 0 ? 8000 : 3000);
                              }
                            } catch (err) {
                              console.error('Error polling:', err);
                            }
                          }, 3000);
                          
                          // Safety timeout
                          setTimeout(() => clearInterval(pollInterval), 600000); // 10 min max
                        } catch (error) {
                          console.error('Bulk training error:', error);
                          toast.error('Failed to start bulk training');
                        }
                      } else {
                        // Train individually for 1-2 coins
                        coinsToTrain.forEach(coin => trainModel(coin));
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FFD600] text-black hover:bg-[#FFD600]/90 transition-all"
                    data-testid="train-models-btn"
                  >
                    🎓 Train Models {selectedCoins.length > 5 && `(${selectedCoins.length})`}
                  </button>
                  
                  <div className="flex gap-2 items-center">
                  <button
                    onClick={async () => {
                      if (!isAuthenticated) {
                        setShowLoginModal(true);
                        toast.info('Please login to run backtest');
                        return;
                      }
                      
                      // V2 Backtest: Only single coin supported
                      if (selectedCoins.length === 0) {
                        toast.error('Please select one coin first');
                        return;
                      }
                      
                      if (selectedCoins.length > 1) {
                        toast.error('Please select only ONE coin for backtest. Multi-coin backtest coming soon.');
                        return;
                      }
                      
                      const coin = selectedCoins[0];
                      
                      // Check if coin needs training
                      if (!trainedCoins.includes(coin)) {
                        const proceed = window.confirm(
                          `⚠️ Model Not Trained\n\n` +
                          `${coin} needs training first.\n\n` +
                          `📝 REQUIRED WORKFLOW:\n` +
                          `1. Click "🎓 Train Models" button\n` +
                          `2. Wait for training to complete (~3-5 min)\n` +
                          `3. Then click "📊 Backtest Models"\n\n` +
                          `⚠️ If you continue now, backtest may fail.\n\n` +
                          `Do you want to continue anyway?`
                        );
                        
                        if (!proceed) {
                          return;
                        }
                      }
                      
                      // Check model age and warn if older than 7 days
                      try {
                        const mlStatus = await axios.get(`${API}/ml/status`);
                        const modelAges = mlStatus.data.model_ages || {};
                        const modelInfo = modelAges[coin];
                        
                        if (modelInfo && modelInfo.age_days > 7) {
                          const proceedOldModel = window.confirm(
                            `⚠️ Model May Be Outdated\n\n` +
                            `The ${coin} model was trained ${modelInfo.age_days} days ago.\n\n` +
                            `For best results, consider retraining if:\n` +
                            `• Market conditions have changed significantly\n` +
                            `• You want to incorporate recent price data\n\n` +
                            `💡 TIP: Click "🎓 Train Models" to retrain with latest data.\n\n` +
                            `Continue with current model anyway?`
                          );
                          
                          if (!proceedOldModel) {
                            return;
                          }
                        }
                      } catch (err) {
                        console.log('Could not check model age:', err);
                        // Continue anyway if we can't check
                      }
                      
                      const confirmed = window.confirm(
                        `📊 Run Backtest V2\n\n` +
                        `This will test the ML models against ${backtestPeriod} days of historical data for ${coin}.\n\n` +
                        `• Evaluates BUY/SELL signal accuracy\n` +
                        `• Simulates MTF confirmation\n` +
                        `• Calculates SQS scores\n` +
                        `• Calculates win rate and P&L\n` +
                        `• Takes ~5-15 seconds\n\n` +
                        `Continue?`
                      );
                      
                      if (!confirmed) return;
                      
                      toast.info(`🔄 Starting ${backtestPeriod}-day backtest for ${coin}...`);
                      
                      try {
                        // Set running status
                        setBacktestStatus({ [coin]: { status: 'running', progress: 50 } });
                        
                        // Call V2 backtest API - settings loaded from user_settings in backend
                        const response = await axios.post(`${API}/backtest/v2/run`, {
                          coin: coin,
                          backtest_days: backtestPeriod
                          // TP, SL, fees, position_size loaded from Settings > Backtesting section
                        });
                        
                        const { run_id, status } = response.data;
                        
                        if (status === 'completed') {
                          // Get full results
                          const resultsResponse = await axios.get(`${API}/backtest/v2/results/${run_id}`);
                          const results = resultsResponse.data;
                          const summary = results.summary || {};
                          
                          setBacktestStatus({ [coin]: { status: 'completed', progress: 100 } });
                          
                          toast.success(
                            `✅ Backtest Complete: ${coin}\n` +
                            `📊 ${summary.total_trades || 0} trades | ${(summary.win_rate || 0).toFixed(1)}% win rate\n` +
                            `💰 PnL: $${(summary.total_pnl || 0).toFixed(2)}`
                          );
                          
                          // Show detailed results in alert
                          // Build score analysis section if available
                          const scoreAnalysis = summary.score_analysis || {};
                          const hasScoreAnalysis = scoreAnalysis.combined_score;
                          
                          let scoreSection = '';
                          if (hasScoreAnalysis) {
                            const cs = scoreAnalysis.combined_score;
                            const ts = scoreAnalysis.technical_score;
                            const ai = scoreAnalysis.ai_score;
                            const threshold = scoreAnalysis.signal_threshold || 0.15;
                            const holdReasons = scoreAnalysis.hold_reasons || {};
                            
                            scoreSection = `\n🔬 SCORE ANALYSIS\n` +
                              `• Signal Threshold: ±${threshold}\n` +
                              `• Combined Score: ${cs.min?.toFixed(3)} to ${cs.max?.toFixed(3)} (avg: ${cs.avg?.toFixed(3)})\n` +
                              `• Technical Score: ${ts.min?.toFixed(3)} to ${ts.max?.toFixed(3)} (avg: ${ts.avg?.toFixed(3)})\n` +
                              `• AI Model Score: ${ai.min?.toFixed(3)} to ${ai.max?.toFixed(3)} (avg: ${ai.avg?.toFixed(3)})\n` +
                              `• Below Threshold: ${holdReasons.below_threshold || 0}/${summary.total_signals || 0} signals\n`;
                            
                            if (scoreAnalysis.explanation) {
                              scoreSection += `\n💡 WHY MOSTLY HOLD:\n${scoreAnalysis.explanation}\n`;
                            }
                          }
                          
                          window.alert(
                            `📊 BACKTEST RESULTS: ${coin}\n` +
                            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
                            `📈 SUMMARY\n` +
                            `• Total Signals: ${summary.total_signals || 0}\n` +
                            `• Executed Trades: ${summary.total_trades || 0}\n` +
                            `• Win Rate: ${(summary.win_rate || 0).toFixed(1)}%\n` +
                            `• Total PnL: $${(summary.total_pnl || 0).toFixed(2)}\n` +
                            `• Sharpe Ratio: ${(summary.sharpe_ratio || 0).toFixed(2)}\n` +
                            `• Max Drawdown: $${(summary.max_drawdown || 0).toFixed(2)}\n\n` +
                            `📋 SIGNAL BREAKDOWN\n` +
                            `• HOLD Signals: ${summary.hold_signals || 0}\n` +
                            `• MTF Wait: ${summary.wait_signals || 0}\n` +
                            `• MTF Skip: ${summary.skip_signals || 0}\n` +
                            `• Low Confidence: ${summary.low_conf_signals || 0}\n` +
                            scoreSection +
                            (summary.total_trades > 0 ? 
                              `\n💹 TRADE STATS\n` +
                              `• Best Trade: $${(summary.best_trade || 0).toFixed(2)}\n` +
                              `• Worst Trade: $${(summary.worst_trade || 0).toFixed(2)}\n` +
                              `• Avg Hold: ${(summary.avg_holding_minutes || 0).toFixed(0)} min\n` +
                              `• Profit Factor: ${(summary.profit_factor || 0).toFixed(2)}\n` : '') +
                            `\nRun ID: ${run_id}`
                          );
                          
                          // Trigger refresh
                          setPerformanceRefreshTrigger(prev => prev + 1);
                        } else {
                          setBacktestStatus({ [coin]: { status: 'error', error: 'Unexpected status' } });
                          toast.error(`Backtest returned unexpected status: ${status}`);
                        }
                        
                        // Clear status after delay
                        setTimeout(() => setBacktestStatus({}), 3000);
                        
                      } catch (error) {
                        console.error('Backtest error:', error);
                        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
                        setBacktestStatus({ [coin]: { status: 'error', error: errorMsg } });
                        toast.error(`❌ Backtest failed: ${errorMsg}`);
                        setTimeout(() => setBacktestStatus({}), 5000);
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#9333EA] text-white hover:bg-[#9333EA]/90 transition-all"
                  >
                    📊 Backtest Models
                  </button>
                  
                  {/* Backtest Period Selector - grouped with Backtest button */}
                  <select
                    value={backtestPeriod}
                    onChange={(e) => {
                      const newPeriod = parseInt(e.target.value);
                      setBacktestPeriod(newPeriod);
                      localStorage.setItem("backtestPeriod", newPeriod.toString());
                      toast.success(`Backtest period set to ${newPeriod} days`);
                    }}
                    className="px-3 py-2 rounded-lg text-sm font-semibold bg-black/30 border border-white/10 text-white hover:bg-black/40 transition-all cursor-pointer focus:outline-none focus:border-primary"
                    title="Backtest Period (max 30 days - CMC data limit)"
                  >
                    <option value="7">7 Days</option>
                    <option value="14">14 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                  </div>
                  
                  <button
                    onClick={async () => {
                      if (!isAuthenticated) {
                        setShowLoginModal(true);
                        toast.info('Please login to retrain models');
                        return;
                      }
                      // Debug: Check current state
                      console.log('🔄 Retrain clicked - selectedCoins:', selectedCoins);
                      
                      if (selectedCoins.length === 0) {
                        toast.error('Please select at least one coin first');
                        return;
                      }
                      
                      // Show immediate feedback
                      toast.info('🔄 Processing retrain request...');
                      
                      const confirmed = window.confirm(
                        `🔄 Force Retrain Models\n\n` +
                        `This will delete existing models and retrain from scratch for ${selectedCoins.length} selected coins.\n\n` +
                        `This is useful if:\n` +
                        `• You want fresh models with latest data\n` +
                        `• Models seem inaccurate\n` +
                        `• You want to clear cached training\n\n` +
                        `⚠️ This will take ~${Math.ceil(selectedCoins.length / 3)} minutes.\n\n` +
                        `Continue?`
                      );
                      
                      if (!confirmed) return;
                      
                      toast.info(`🔄 Force retraining ${selectedCoins.length} coins...`);
                      
                      // Clear ML signals from state
                      setMlSignals({});
                      
                      // Set all to queued IMMEDIATELY
                      const newStatuses = {};
                      selectedCoins.forEach(coin => {
                        newStatuses[coin] = { status: "queued", progress: 0 };
                      });
                      setTrainingStatus(prev => {
                        const updated = { ...prev, ...newStatuses };
                        console.log('🔄 RETRAIN: Set initial status:', updated);
                        return updated;
                      });
                      
                      // Force UI update delay
                      await new Promise(resolve => setTimeout(resolve, 100));
                      
                      // Use bulk endpoint with force=true
                      try {
                        const payload = selectedCoins.length > 2 ? selectedCoins : selectedCoins;
                        await axios.post(`${API}/ml/train-bulk?force=true`, payload);
                        
                        // Poll for all coins
                        let generatingSignals = false;
                        const pollInterval = setInterval(async () => {
                          try {
                            const statusResponse = await axios.get(`${API}/ml/training-status`);
                            const allStatuses = statusResponse.data;
                            
                            console.log('🔄 RETRAIN: Polling update:', allStatuses);
                            
                            setTrainingStatus(prev => {
                              const updated = { ...prev, ...allStatuses };
                              console.log('🔄 RETRAIN: Updated state:', updated);
                              return updated;
                            });
                            
                            const allDone = selectedCoins.every(coin => 
                              allStatuses[coin]?.status === 'completed' || allStatuses[coin]?.status === 'error'
                            );
                            
                            if (allDone && !generatingSignals) {
                              clearInterval(pollInterval);
                              generatingSignals = true;
                              
                              const completed = selectedCoins.filter(c => allStatuses[c]?.status === 'completed').length;
                              const failed = selectedCoins.filter(c => allStatuses[c]?.status === 'error');
                              
                              if (failed.length > 0) {
                                const failedDetails = failed.map(c => {
                                  const err = allStatuses[c]?.error || 'Unknown error';
                                  return `${c}: ${err}`;
                                }).join(', ');
                                toast.warning(
                                  `Retraining: ${completed}/${selectedCoins.length} succeeded. Failed: ${failedDetails}`,
                                  { duration: 8000 }
                                );
                              } else {
                                toast.success(`✅ Retraining complete! ${completed}/${selectedCoins.length} coins`);
                              }
                              
                              // Refresh trained coins list
                              await fetchTrainedCoins();
                              
                              // Clear training status
                              setTimeout(() => setTrainingStatus({}), failed.length > 0 ? 8000 : 3000);
                            }
                          } catch (err) {
                            console.error('Error polling:', err);
                          }
                        }, 3000);
                        
                        setTimeout(() => clearInterval(pollInterval), 600000);
                      } catch (error) {
                        console.error('Retrain error:', error);
                        toast.error('Failed to start retraining');
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF0055] text-white hover:bg-[#FF0055]/90 transition-all"
                  >
                    🔄 Retrain Models
                  </button>
                  
                  <button
                    onClick={async () => {
                      if (!isAuthenticated) {
                        setShowLoginModal(true);
                        toast.info('Please login to generate ML signals');
                        return;
                      }
                      // Generate ML signals for selected coins
                      if (selectedCoins.length === 0) {
                        toast.error('Please select at least one coin first');
                        return;
                      }
                      
                      // Check which coins have trained models
                      const untrainedCoins = selectedCoins.filter(coin => !trainedCoins.includes(coin));
                      
                      if (untrainedCoins.length > 0) {
                        toast.error(
                          `⚠️ Cannot generate signals!\n\n` +
                          `${untrainedCoins.length} coin(s) need training first: ${untrainedCoins.join(', ')}\n\n` +
                          `Click "🎓 Train Models" first.`,
                          { duration: 6000 }
                        );
                        return;
                      }
                      
                      const confirmed = window.confirm(
                        `🎯 Generate ML Signals\n\n` +
                        `This will generate trading signals for ${selectedCoins.length} selected coins using trained ML models.\n\n` +
                        `• Uses LSTM + Gradient Boosting ensemble\n` +
                        `• Includes technical analysis\n` +
                        `• Adjusts for market sentiment (F&G Index)\n` +
                        `• Takes ~30 seconds\n\n` +
                        `Continue?`
                      );
                      
                      if (!confirmed) return;
                      
                      toast.info(`🎯 Generating ML signals for ${selectedCoins.length} coins...`);
                      
                      // Set all to queued IMMEDIATELY
                      const newStatuses = {};
                      selectedCoins.forEach(coin => {
                        newStatuses[coin] = { status: "queued", progress: 0 };
                      });
                      setSignalGenerationStatus(prev => ({ ...prev, ...newStatuses }));
                      
                      try {
                        // Call bulk signal generation endpoint
                        await axios.post(`${API}/ml/signals/bulk`, selectedCoins);
                        
                        // Poll signal generation status
                        let pollCount = 0;
                        const maxPolls = 60; // 60 polls * 2s = 2 minutes max
                        const signalPollInterval = setInterval(async () => {
                          try {
                            pollCount++;
                            const signalStatusResponse = await axios.get(`${API}/ml/signal-generation-status`);
                            const signalStatuses = signalStatusResponse.data;
                            
                            console.log(`📊 Signal generation status (poll ${pollCount}):`, signalStatuses);
                            
                            setSignalGenerationStatus(prev => ({ ...prev, ...signalStatuses }));
                            
                            // Check if max polling time reached
                            if (pollCount >= maxPolls) {
                              clearInterval(signalPollInterval);
                              const stuckCoins = selectedCoins.filter(coin => 
                                !signalStatuses[coin] || 
                                (signalStatuses[coin]?.status !== 'completed' && signalStatuses[coin]?.status !== 'error')
                              );
                              if (stuckCoins.length > 0) {
                                toast.error(`⏱️ Timeout: ${stuckCoins.join(', ')} took too long. Try generating individually.`, { duration: 8000 });
                              }
                              setTimeout(() => setSignalGenerationStatus({}), 3000);
                              return;
                            }
                            
                            const allSignalsDone = selectedCoins.every(coin =>
                              signalStatuses[coin]?.status === 'completed' || signalStatuses[coin]?.status === 'error'
                            );
                            
                            if (allSignalsDone) {
                              clearInterval(signalPollInterval);
                              
                              const completedCoins = selectedCoins.filter(c => signalStatuses[c]?.status === 'completed');
                              const errorCoins = selectedCoins.filter(c => signalStatuses[c]?.status === 'error');
                              
                              console.log('✅ Completed coins:', completedCoins);
                              console.log('❌ Error coins:', errorCoins);
                              
                              // Fetch all generated signals
                              for (const coin of completedCoins) {
                                try {
                                  await fetchMLSignal(coin);
                                } catch (err) {
                                  console.error(`Failed to fetch signal for ${coin}:`, err);
                                  toast.error(`Failed to fetch ${coin} signal`);
                                }
                              }
                              
                              // Store signals in manual trading table
                              if (completedCoins.length > 0) {
                                try {
                                  const manualResponse = await axios.post(`${API}/manual/generate-signals`, completedCoins);
                                  console.log('📝 Stored in manual table:', manualResponse.data);
                                  fetchManualStats(); // Refresh manual stats
                                  // Trigger immediate refresh of PerformanceTracker
                                  setPerformanceRefreshTrigger(prev => prev + 1);
                                } catch (err) {
                                  console.error('Failed to store in manual table:', err);
                                }
                              }
                              
                              // Show success message
                              if (completedCoins.length > 0) {
                                toast.success(`✅ Generated ML signals for ${completedCoins.length}/${selectedCoins.length} coins!`);
                              }
                              
                              // Show error details for failed coins
                              if (errorCoins.length > 0) {
                                errorCoins.forEach(coin => {
                                  const error = signalStatuses[coin]?.error || 'Unknown error';
                                  console.error(`Signal generation failed for ${coin}:`, error);
                                  toast.error(`❌ ${coin}: ${error}`, { duration: 5000 });
                                });
                              }
                              
                              // Clear signal generation status
                              setTimeout(() => setSignalGenerationStatus({}), 5000);
                            }
                          } catch (err) {
                            console.error('Error polling signal generation status:', err);
                          }
                        }, 2000); // Poll every 2 seconds
                        
                        // Safety timeout
                        setTimeout(() => clearInterval(signalPollInterval), 120000); // 2 min max
                      } catch (error) {
                        console.error('Signal generation error:', error);
                        toast.error('Failed to start signal generation');
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#00FF94] text-black hover:bg-[#00FF94]/90 transition-all"
                  >
                    🎯 Generate ML Signals
                  </button>
                  
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        setShowLoginModal(true);
                        toast.info('Please login to close positions');
                        return;
                      }
                      closeAllManualPositions();
                    }}
                    disabled={closingManualPositions || (manualStats && manualStats.open_positions === 0)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      closingManualPositions || (manualStats && manualStats.open_positions === 0)
                        ? 'bg-gray-500/50 text-gray-400 cursor-not-allowed'
                        : 'bg-[#FF0055] text-white hover:bg-[#FF0055]/90'
                    }`}
                  >
                    {closingManualPositions ? '⏳ Closing...' : `🔒 Close Manual Signals${manualStats?.open_positions ? ` (${manualStats.open_positions})` : ''}`}
                  </button>
                  
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        setShowLoginModal(true);
                        toast.info('Please login to close positions');
                        return;
                      }
                      closeAllAutoPositions();
                    }}
                    disabled={closingAutoPositions || autoOpenPositions === 0}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      closingAutoPositions || autoOpenPositions === 0
                        ? 'bg-gray-500/50 text-gray-400 cursor-not-allowed'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {closingAutoPositions ? '⏳ Closing...' : `⚡ Close Auto Signals${autoOpenPositions ? ` (${autoOpenPositions})` : ''}`}
                  </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard */}
      <div className="container mx-auto px-4 py-6">
        <Tabs defaultValue="signals" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="signals" data-testid="tab-signals">Signals</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">
              <Award className="w-4 h-4 mr-2" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
            <TabsTrigger value="metadata" data-testid="tab-metadata">
              <Database className="w-4 h-4 mr-2" />
              MetaModelData
            </TabsTrigger>
            <TabsTrigger value="mtf-analysis" data-testid="tab-mtf-analysis">
              <BarChart3 className="w-4 h-4 mr-2" />
              MTF Analysis
            </TabsTrigger>
            <TabsTrigger value="model-metrics" data-testid="tab-model-metrics">
              <Activity className="w-4 h-4 mr-2" />
              Model Metrics
            </TabsTrigger>
            <TabsTrigger value="system-monitor" data-testid="tab-system-monitor" className={!isAdmin ? "hidden" : ""}>
              <Activity className="w-4 h-4 mr-2" />
              System Monitor
            </TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news">📰 News</TabsTrigger>
          </TabsList>

          <TabsContent value="signals">
            {/* Debug: Show active training status */}
            {Object.keys(trainingStatus).length > 0 && (
              <div className="glass-card p-4 border-[#FFD600] mb-4">
                <h4 className="text-sm font-semibold text-[#FFD600] mb-2">🔄 Training Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(trainingStatus).map(([coin, status]) => (
                    <div key={coin} className="text-muted-foreground">
                      {coin}: <span className={
                        status.status === 'completed' ? 'text-green-400' :
                        status.status === 'error' ? 'text-red-400' :
                        status.status === 'training' ? 'text-yellow-400' :
                        'text-data'
                      }>
                        {status.status === 'error' ? `❌ ${status.error || 'Failed'}` : 
                         status.status === 'completed' ? '✅ Done' :
                         `${status.status} (${status.progress}%)`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Debug: Show signal generation status */}
            {Object.keys(signalGenerationStatus).length > 0 && (
              <div className="glass-card p-4 border-[#00FF94] mb-4">
                <h4 className="text-sm font-semibold text-[#00FF94] mb-2">🎯 Signal Generation Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(signalGenerationStatus).map(([coin, status]) => (
                    <div key={coin} className="text-muted-foreground">
                      {coin}: <span className="text-data">{status.status} ({status.progress}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Debug: Show backtest status */}
            {Object.keys(backtestStatus).length > 0 && (
              <div className="glass-card p-4 border-[#9333EA] mb-4">
                <h4 className="text-sm font-semibold text-[#9333EA] mb-2">📊 Backtest Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(backtestStatus).map(([coin, status]) => (
                    <div key={coin} className="text-muted-foreground">
                      {coin}: <span className="text-data">{status.status} ({status.progress}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Price Load Error Banner */}
            {priceLoadError && !prices && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-red-400">Failed to load price data</p>
                    <p className="text-sm text-muted-foreground">Unable to fetch crypto prices from the API</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPriceLoadError(false);
                    fetchPrices();
                  }}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-medium transition-colors"
                >
                  Retry
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Price Cards */}
              {prices && selectedCoins.map(coin => (
                prices[coin] && <PriceCard key={coin} coin={coin} data={prices[coin]} />
              ))}
            </div>

            {/* SECTION 1: Basic Technical Signals - Only visible when Basic tab is active */}
            {activeSignalType === 'basic' && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold heading-font flex items-center gap-2">
                    <Activity className="w-6 h-6 text-[#00FF94]" />
                    📊 Basic Technical Signals
                  </h2>
                  <span className="text-sm text-muted-foreground">Real-time technical analysis</span>
                </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {selectedCoins.map(coin => {
                  const currentSignal = signals[coin];
                  
                  // Show basic signal card if exists
                  if (currentSignal) {
                    return (
                      <SignalCard
                        key={coin}
                        signal={currentSignal}
                        onRefresh={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to refresh signals');
                            return;
                          }
                          fetchSignal(coin);
                        }}
                        loading={signalLoading[coin]}
                        isML={false}
                      />
                    );
                  }
                  
                  // Show generate button if no basic signal
                  return (
                    <div key={coin} className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up text-center" data-testid={`no-basic-signal-${coin.toLowerCase()}`}>
                      <h3 className="text-xl font-bold heading-font mb-4">{coin}</h3>
                      <p className="text-muted-foreground mb-4">No signal generated yet</p>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to generate signals');
                            return;
                          }
                          fetchSignal(coin);
                        }}
                        disabled={signalLoading[coin]}
                        className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                        data-testid={`generate-basic-signal-${coin.toLowerCase()}`}
                      >
                        {signalLoading[coin] ? "Generating..." : "Generate Signal"}
                      </button>
                    </div>
                  );
                })}
              </div>
              </div>
            )}

            {/* SECTION 2: ML-Powered Signals & Training - Only visible when ML tab is active */}
            {activeSignalType === 'ml' && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold heading-font flex items-center gap-2">
                    <Activity className="w-6 h-6 text-[#FFD600]" />
                    🤖 ML-Powered Signals & Training
                  </h2>
                  <span className="text-sm text-muted-foreground">AI-driven predictions with backtesting</span>
                </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {selectedCoins.map(coin => {
                  const mlSignal = mlSignals[coin];
                  const training = trainingStatus[coin];
                  const signalGen = signalGenerationStatus[coin];
                  const backtest = backtestStatus[coin];
                  const isTraining = training && (training.status === "training" || training.status === "queued");
                  const isGeneratingSignal = signalGen && (signalGen.status === "generating" || signalGen.status === "queued");
                  const isBacktesting = backtest && (backtest.status === "running" || backtest.status === "queued" || backtest.status === "saving");
                  
                  // Show backtest progress if running
                  if (isBacktesting) {
                    return (
                      <div key={coin} className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up text-center" data-testid={`backtest-${coin.toLowerCase()}`}>
                        <h3 className="text-xl font-bold heading-font mb-4">{coin}</h3>
                        <div className="space-y-4">
                          <p className="text-[#9333EA] mb-2 font-semibold">
                            {backtest.status === "queued" ? "⏳ Queued for Backtest..." : 
                             backtest.status === "saving" ? "💾 Saving Results..." : `📊 Running ${backtestPeriod}-Day Backtest...`}
                          </p>
                          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-[#9333EA] to-[#FFD600] h-full transition-all duration-500 ease-out"
                              style={{ width: `${backtest.progress || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {backtest.status === "queued" ? "Waiting to start..." : `${backtest.progress || 0}% complete`}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Show training progress if coin is training/queued
                  if (isTraining) {
                    return (
                      <div key={coin} className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up text-center" data-testid={`training-${coin.toLowerCase()}`}>
                        <h3 className="text-xl font-bold heading-font mb-4">{coin}</h3>
                        <div className="space-y-4">
                          <p className="text-[#FFD600] mb-2 font-semibold">
                            {training.status === "queued" ? "⏳ Queued for Training..." : "🎓 Training Models..."}
                          </p>
                          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-[#FFD600] to-[#00FF94] h-full transition-all duration-500 ease-out"
                              style={{ width: `${training.progress || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {training.status === "queued" ? "Waiting to start..." : `${training.progress || 0}% complete`}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Show signal generation progress if generating
                  if (isGeneratingSignal) {
                    return (
                      <div key={coin} className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up text-center" data-testid={`generating-signal-${coin.toLowerCase()}`}>
                        <h3 className="text-xl font-bold heading-font mb-4">{coin}</h3>
                        <div className="space-y-4">
                          <p className="text-[#00FF94] mb-2 font-semibold">
                            {signalGen.status === "queued" ? "⏳ Queued for Signal Generation..." : "🎯 Generating ML Signal..."}
                          </p>
                          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-[#00FF94] to-[#FFD600] h-full transition-all duration-500 ease-out"
                              style={{ width: `${signalGen.progress || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {signalGen.status === "queued" ? "Waiting to start..." : `${signalGen.progress || 0}% complete`}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Show ML signal card if exists
                  if (mlSignal) {
                    return (
                      <SignalCard
                        key={coin}
                        signal={mlSignal}
                        onRefresh={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to refresh ML signals');
                            return;
                          }
                          fetchMLSignal(coin);
                        }}
                        loading={signalLoading[coin]}
                        isML={true}
                      />
                    );
                  }
                  
                  // Show generate ML signal button if no ML signal and not training/generating
                  return (
                    <div key={coin} className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up text-center" data-testid={`no-ml-signal-${coin.toLowerCase()}`}>
                      <h3 className="text-xl font-bold heading-font mb-4">{coin}</h3>
                      <p className="text-muted-foreground mb-4">
                        No ML signal available. Train models first.
                      </p>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setShowLoginModal(true);
                            toast.info('Please login to generate ML signals');
                            return;
                          }
                          fetchMLSignal(coin);
                        }}
                        disabled={signalLoading[coin]}
                        className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                        data-testid={`generate-ml-signal-${coin.toLowerCase()}`}
                      >
                        {signalLoading[coin] ? "Generating..." : "Generate ML Signal"}
                      </button>
                    </div>
                  );
                })}
              </div>
              </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              {selectedCoins.slice(0, 4).map(coin => (
                <HistoricalChart key={coin} coin={coin} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="performance">
            {/* Login Required Check */}
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view performance data and trading statistics.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login to Continue
                </button>
              </div>
            ) : (
              <>
            {/* Automated Trading Stats */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <span className="text-primary">🤖</span> Automated Trading Stats
                </h3>
                <div className="flex items-center gap-2" data-testid="stats-period-selector">
                  <label htmlFor="stats-period" className="text-xs text-muted-foreground">
                    Period:
                  </label>
                  <select
                    id="stats-period"
                    data-testid="stats-period-select"
                    value={statsPeriod}
                    onChange={(e) => setStatsPeriod(parseInt(e.target.value))}
                    className="bg-black/40 border border-white/10 text-data text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary cursor-pointer"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={0}>All time</option>
                  </select>
                </div>
              </div>
              <PerformanceStats days={statsPeriod} />
              
              {/* Effective Capital Display - Compounding Info */}
              {autoCapital && (
                <div className="glass-card p-4 rounded-xl border border-white/10 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">💰</span>
                    <span className="text-sm font-medium text-muted-foreground">Effective Capital (Compounding)</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-black/20 rounded-lg">
                      <div className="text-xs text-muted-foreground">Base Capital</div>
                      <div className="text-lg font-bold text-data">
                        ${autoCapital.initial?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="p-2 bg-black/20 rounded-lg">
                      <div className="text-xs text-muted-foreground">Realized PNL</div>
                      <div className={`text-lg font-bold ${autoCapital.realized_pnl >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                        {autoCapital.realized_pnl >= 0 ? '+' : ''}${autoCapital.realized_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="p-2 bg-black/20 rounded-lg">
                      <div className="text-xs text-muted-foreground">Effective Capital</div>
                      <div className={`text-lg font-bold ${autoCapital.effective >= autoCapital.initial ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                        ${autoCapital.effective?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="p-2 bg-black/20 rounded-lg">
                      <div className="text-xs text-muted-foreground">Max Per Position</div>
                      <div className="text-lg font-bold text-data">
                        ${(autoCapital.effective * 0.5)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-xs text-muted-foreground">(50% cap)</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Manual Trading Stats */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="text-primary">🎯</span> Manual Trading Stats
              </h3>
              <div className="glass-card p-4 rounded-xl border border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 rounded-lg bg-black/20">
                    <div className="text-2xl font-bold text-data">
                      {manualStats ? `${manualStats.accuracy_percentage}%` : '--'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Accuracy ({manualStats?.correct_signals || 0}/{manualStats?.total_signals || 0})
                    </div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-black/20">
                    <div className={`text-2xl font-bold ${manualStats?.total_profit_loss >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                      ${manualStats?.total_profit_loss?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-muted-foreground">Total P/L</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-black/20">
                    <div className={`text-2xl font-bold ${manualStats?.avg_profit_loss >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                      ${manualStats?.avg_profit_loss?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg P/L</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-black/20">
                    <div className="text-2xl font-bold text-[#FFD600]">
                      {manualStats?.best_signal ? `${manualStats.best_signal.coin} ${manualStats.best_signal.signal}` : '--'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Best Signal {manualStats?.best_signal ? `($${manualStats.best_signal.profit_loss.toFixed(2)})` : ''}
                    </div>
                  </div>
                </div>
                {manualStats?.open_positions > 0 && (
                  <div className="mt-3 text-center text-sm text-blue-400">
                    📊 {manualStats.open_positions} open position(s)
                  </div>
                )}
                
                {/* Manual Effective Capital Display - Compounding Info */}
                {manualStats?.capital && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">💰</span>
                      <span className="text-sm font-medium text-muted-foreground">Effective Capital (Compounding)</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-2 bg-black/30 rounded-lg">
                        <div className="text-xs text-muted-foreground">Base Capital</div>
                        <div className="text-lg font-bold text-data">
                          ${manualStats.capital.initial?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="p-2 bg-black/30 rounded-lg">
                        <div className="text-xs text-muted-foreground">Realized PNL</div>
                        <div className={`text-lg font-bold ${manualStats.capital.realized_pnl >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          {manualStats.capital.realized_pnl >= 0 ? '+' : ''}${manualStats.capital.realized_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="p-2 bg-black/30 rounded-lg">
                        <div className="text-xs text-muted-foreground">Effective Capital</div>
                        <div className={`text-lg font-bold ${manualStats.capital.effective >= manualStats.capital.initial ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ${manualStats.capital.effective?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="p-2 bg-black/30 rounded-lg">
                        <div className="text-xs text-muted-foreground">Max Per Position</div>
                        <div className="text-lg font-bold text-data">
                          ${(manualStats.capital.effective * 0.5)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-muted-foreground">(50% cap)</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Backtest Stats */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="text-primary">📊</span> Backtest Stats
                {backtestStats?.coins_tested > 0 && (
                  <span className="text-xs text-muted-foreground">({backtestStats.coins_tested} coins tested)</span>
                )}
              </h3>
              <div className="glass-card p-4 rounded-xl border border-white/10">
                {backtestStats?.total_signals > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 rounded-lg bg-black/20">
                        <div className="text-2xl font-bold text-data">
                          {backtestStats?.accuracy_rate || 0}%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Win Rate ({backtestStats?.correct_signals || 0}/{backtestStats?.total_signals || 0})
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-black/20">
                        <div className={`text-2xl font-bold ${backtestStats?.total_profit_loss >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ${backtestStats?.total_profit_loss?.toFixed(2) || '0.00'}
                        </div>
                        <div className="text-xs text-muted-foreground">Total P/L</div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-black/20">
                        <div className={`text-2xl font-bold ${backtestStats?.avg_profit_loss >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          ${backtestStats?.avg_profit_loss?.toFixed(2) || '0.00'}
                        </div>
                        <div className="text-xs text-muted-foreground">Avg P/L per Trade</div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-black/20">
                        <div className={`text-2xl font-bold ${backtestStats?.avg_sharpe_ratio >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                          {backtestStats?.avg_sharpe_ratio || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">Avg Sharpe Ratio</div>
                      </div>
                    </div>
                    {/* Exit Breakdown */}
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="text-center p-2 rounded-lg bg-green-500/10">
                        <div className="text-lg font-bold text-green-400">
                          🎯 {backtestStats?.total_tp_exits || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">Take Profit Exits</div>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-red-500/10">
                        <div className="text-lg font-bold text-red-400">
                          🛑 {backtestStats?.total_sl_exits || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">Stop Loss Exits</div>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-yellow-500/10">
                        <div className="text-lg font-bold text-yellow-400">
                          ⏰ {backtestStats?.total_time_exits || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">Time Exits</div>
                      </div>
                    </div>
                    {/* Best/Worst Trade */}
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {backtestStats?.best_signal && (
                        <div className="p-2 rounded-lg bg-green-500/10 text-center">
                          <div className="text-sm font-semibold text-green-400">
                            Best: {backtestStats.best_signal.coin} {backtestStats.best_signal.signal}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            +${backtestStats.best_signal.profit_loss?.toFixed(2)} ({backtestStats.best_signal.exit_reason})
                          </div>
                        </div>
                      )}
                      {backtestStats?.worst_signal && (
                        <div className="p-2 rounded-lg bg-red-500/10 text-center">
                          <div className="text-sm font-semibold text-red-400">
                            Worst: {backtestStats.worst_signal.coin} {backtestStats.worst_signal.signal}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            ${backtestStats.worst_signal.profit_loss?.toFixed(2)} ({backtestStats.worst_signal.exit_reason})
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm">No backtest results yet.</p>
                    <p className="text-xs mt-1">Run backtests from the Dashboard to see historical performance.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Performance Tracker with dropdown */}
            <PerformanceTracker refreshTrigger={performanceRefreshTrigger} />
              </>
            )}
          </TabsContent>

          <TabsContent value="history">
            {/* Login Required Check */}
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view signal history and trading records.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login to Continue
                </button>
              </div>
            ) : (
              <SignalHistory />
            )}
          </TabsContent>

          <TabsContent value="metadata">
            {/* Login Required Check for Meta Data */}
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view meta model data.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login to Continue
                </button>
              </div>
            ) : (
              <MetaDataTable />
            )}
          </TabsContent>

          <TabsContent value="mtf-analysis">
            {/* Admin Only - MTF Analysis */}
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view MTF performance analysis.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login to Continue
                </button>
              </div>
            ) : !isAdmin ? (
              <div className="glass-card p-8 rounded-xl border border-yellow-500/30 text-center">
                <Shield className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-yellow-400">Admin Access Required</h3>
                <p className="text-muted-foreground mb-4">
                  MTF Performance Analysis is restricted to admin users only.
                </p>
              </div>
            ) : (
              <MtfAnalysisTab />
            )}
          </TabsContent>

          {/* Model Metrics Tab - Login Required */}
          <TabsContent value="model-metrics">
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view Model Metrics.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login
                </button>
              </div>
            ) : (
              <ModelMetricsTab />
            )}
          </TabsContent>

          {/* Admin Only - System Monitor */}
          <TabsContent value="system-monitor">
            {!isAuthenticated ? (
              <div className="glass-card p-8 rounded-xl border border-white/10 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please login to view System Monitor.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Login to Continue
                </button>
              </div>
            ) : !isAdmin ? (
              <div className="glass-card p-8 rounded-xl border border-yellow-500/30 text-center">
                <Shield className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-yellow-400">Admin Access Required</h3>
                <p className="text-muted-foreground mb-4">
                  System Monitor is restricted to admin users only.
                </p>
              </div>
            ) : (
              <SystemMonitorTab />
            )}
          </TabsContent>

          <TabsContent value="news">
            <NewsTab />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Footer */}
      <footer className="py-4 text-center border-t border-white/10 mt-8">
        <p className="text-xs text-muted-foreground">© 2026 Signal4Strategy | Architected by Shibu Yohannan</p>
      </footer>
      
      {/* Auth Modals */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
      <ChangePasswordModal 
        isOpen={showChangePasswordModal} 
        onClose={() => setShowChangePasswordModal(false)} 
      />
      <ForceChangePasswordModal 
        isOpen={showForceChangePasswordModal} 
        onClose={() => setShowForceChangePasswordModal(false)} 
      />
      <ContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
      
      {/* Trading Assistant Modal */}
      <TradingAssistantModal
        isOpen={showTradingAssistant}
        onClose={() => setShowTradingAssistant(false)}
      />

      {/* SRE Agent Modal */}
      <SREAgentModal
        isOpen={showSREAgent}
        onClose={() => setShowSREAgent(false)}
      />
      
      {/* AI Research Input Dialog */}
      <Dialog open={showAIResearchInput} onOpenChange={setShowAIResearchInput}>
        <DialogContent className="sm:max-w-lg bg-gray-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🔬 AI Research Analyst Agent
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Multi-asset research agent. Analyze Stocks, Crypto, ETFs, FX, Commodities, Indices & Futures.
            </DialogDescription>
            <p className="text-xs text-emerald-400 mt-1">✨ Built using OpenAI Agent Builder platform</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <textarea
              value={aiResearchQuery}
              onChange={(e) => setAiResearchQuery(e.target.value)}
              placeholder="Enter asset symbol or query, e.g.:
• Crypto: BTC, ETH, SOL
• Stock: AAPL, TSLA, NVDA
• Index: S&P 500, NASDAQ, DAX, FTSE 100
• ETF: SPY, VOO, QQQ
• FX: EUR/USD, GBP/JPY
• Commodity: Gold, WTI, Brent, Silver, Natural Gas
• Futures: CL=F, GC=F, ES=F, NQ=F"
              className="w-full h-44 px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none text-sm"
              autoFocus
            />
            <p className="text-xs text-gray-500">
              The AI Research Agent will analyze your query using multiple specialized sub-agents including Technical Indicator Engine, Macro Context Analyst, News Sentiment Extractor, and more.
            </p>
            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
              <span className="text-xs text-emerald-400">✨ Built using OpenAI Agent Builder platform</span>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <button
              onClick={() => setShowAIResearchInput(false)}
              className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleAIResearch}
              disabled={!aiResearchQuery.trim() || aiResearchLoading}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {aiResearchLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Analyzing...
                </>
              ) : (
                <>🔍 Analyze</>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* AI Research Result Dialog */}
      <Dialog open={showAIResearchResult} onOpenChange={setShowAIResearchResult}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] bg-gray-900 border-white/10 overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🔬 Research Analysis
            </DialogTitle>
            {aiResearchResult && (
              <DialogDescription className="text-gray-400 flex items-center gap-4">
                <span>Query: &ldquo;{aiResearchResult.query}&rdquo;</span>
                {aiResearchResult.total_duration_ms && (
                  <span className="text-xs text-emerald-400">⏱️ {(aiResearchResult.total_duration_ms / 1000).toFixed(1)}s</span>
                )}
                {aiResearchResult.asset_type && aiResearchResult.asset_type !== 'unknown' && (
                  <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">{aiResearchResult.asset_type.toUpperCase()}</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-auto py-4">
            {aiResearchResult && (
              <div className="space-y-4">
                {/* Workflow Steps Accordion */}
                {aiResearchResult.workflow_steps && aiResearchResult.workflow_steps.length > 0 && (
                  <div className="mb-6">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors">
                        <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        📋 Workflow Steps ({aiResearchResult.workflow_steps.length} agents)
                      </summary>
                      <div className="mt-3 space-y-3 pl-6 border-l-2 border-cyan-500/30">
                        {aiResearchResult.workflow_steps.map((step, index) => (
                          <details key={index} className="group/step">
                            <summary className="cursor-pointer flex items-center gap-2 text-xs font-medium text-gray-300 hover:text-white transition-colors py-1">
                              <svg className="w-3 h-3 transition-transform group-open/step:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <span className="text-emerald-400">{index + 1}.</span>
                              <span>{step.agent_name}</span>
                              <span className="text-gray-500 text-[10px]">({step.duration_ms}ms)</span>
                            </summary>
                            <div className="mt-2 ml-5 p-3 bg-black/40 rounded-lg border border-white/5 text-xs text-gray-300 whitespace-pre-wrap max-h-60 overflow-auto">
                              {step.output}
                            </div>
                          </details>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
                
                {/* Final Output */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-semibold text-emerald-400 mb-3">📊 Final Research Report</h4>
                  {formatAIResponse(aiResearchResult.response)}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 border-t border-white/10 pt-4">
            <div className="flex-1 text-xs text-emerald-400 flex items-center">
              ✨ Powered by OpenAI Agent Builder
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowAIResearchResult(false);
                  setAiResearchQuery('');
                  setShowAIResearchInput(true);
                }}
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all flex items-center gap-2"
              >
                🔄 New Query
              </button>
              <button
                onClick={() => setShowAIResearchResult(false)}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600"
              >
                Close
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;